// materials/check/Request_new.jsx
// 검수 요청(QC) 전용 폼 — 다른 문서 불러오기 없음

import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
// import RequestSummary from "../../../components/common/materials/RequestSummary"; // 요약 카드

export default function RequestOfficialForm({
  initial = {},
  docLabel = "검수 요청",
  readOnly = false,
  onChange,
}) {
  const [title, setTitle] = useState(initial.title || `${docLabel} 문서`);
  const [inspectionDate, setInspectionDate] = useState(
    initial.inspection_date || ""
  );
  const [inspector, setInspector] = useState(initial.inspector || "");
  const [criteria, setCriteria] = useState(initial.criteria || "");
  const [overall, setOverall] = useState(initial.overall || "PENDING");
  const [note, setNote] = useState(initial.note || "");

  const initialRows =
    (initial.items || []).map((r) => ({ ...r, _id: r._id || uuidv4() })) || [];
  const [items, setItems] = useState(
    initialRows.length
      ? initialRows
      : [
        {
          _id: uuidv4(),
          code: "",
          name: "",
          spec: "",
          unit: "EA",
          qty: 0,
          remark: "",
        },
      ]
  );

  // initial 변경 시 동기화
  useEffect(() => {
    setTitle(initial.title || `${docLabel} 문서`);
    setInspectionDate(initial.inspection_date || "");
    setInspector(initial.inspector || "");
    setCriteria(initial.criteria || "");
    setOverall(initial.overall || "PENDING");
    setNote(initial.note || "");

    const nextRows =
      (initial.items || []).map((r) => ({ ...r, _id: r._id || uuidv4() })) ||
      [];
    setItems(
      nextRows.length
        ? nextRows
        : [
          {
            _id: uuidv4(),
            code: "",
            name: "",
            spec: "",
            unit: "EA",
            qty: 0,
            remark: "",
          },
        ]
    );
  }, [JSON.stringify(initial), docLabel]);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const rows = useMemo(
    () =>
      items.map((r) => ({
        ...r,
        qty: toNum(r.qty),
      })),
    [items]
  );

  const totalQty = useMemo(
    () => rows.reduce((s, r) => s + toNum(r.qty), 0),
    [rows]
  );

  // 패턴 2: QC에서는 결과값 없음 → 합계만 사용
  const totalOk = 0;
  const totalNg = 0;
  const passRate = 0;

  // 요약 카드용
  const summaryForm = useMemo(
    () => ({
      title,
      inspection_date: inspectionDate,
      inspector,
      criteria,
      overall,
      note,
      items: rows.map(({ _id, ...rest }) => rest),
      total_qty: totalQty,
      total_ok: totalOk,
      total_ng: totalNg,
      pass_rate: passRate,
    }),
    [
      title,
      inspectionDate,
      inspector,
      criteria,
      overall,
      note,
      rows,
      totalQty,
      totalOk,
      totalNg,
      passRate,
    ]
  );

  // 🔥 상위(FormContentManager)로 meta 전달
  useEffect(() => {
    if (!onChange) return;

    onChange({
      title,
      header: {
        title,
        inspection_date: inspectionDate || null,
        inspector,
        criteria,
        overall,
        note,
        total_qty: totalQty,
        total_ok: totalOk,
        total_ng: totalNg,
        pass_rate: passRate,
      },
      lines: rows.map(({ _id, ...rest }) => rest),
      // summary_component: <RequestSummary form={summaryForm} />,
    });
  }, [
    title,
    inspectionDate,
    inspector,
    criteria,
    overall,
    note,
    rows,
    totalQty,
    totalOk,
    totalNg,
    passRate,
    onChange,
    summaryForm,
  ]);

  const addRow = () =>
    setItems((p) => [
      ...p,
      {
        _id: uuidv4(),
        code: "",
        name: "",
        spec: "",
        unit: "EA",
        qty: 0,
        remark: "",
      },
    ]);

  const removeRow = (id) => setItems((p) => p.filter((r) => r._id !== id));

  const updateCell = (id, key, value) =>
    setItems((p) =>
      p.map((r) =>
        r._id === id
          ? {
            ...r,
            [key]: key === "qty" ? toNum(value) : value,
          }
          : r
      )
    );

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >{/* 기본 정보 */}
        <div className="space-y-1">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">제목</label>
            <input
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">검수일</label>
            <input
              type="date"
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={inspectionDate}
              onChange={(e) => setInspectionDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">검수자</label>
            <input
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={inspector}
              onChange={(e) => setInspector(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              검수 기준
            </label>
            <textarea
              className="w-full min-h-[80px] border rounded-md px-2 py-1 text-sm"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              전체 판정
            </label>
            <select
              className="w-full border rounded-md px-2 py-1 text-sm"
              value={overall}
              onChange={(e) => setOverall(e.target.value)}
              disabled={readOnly}
            >
              <option value="PENDING">PENDING</option>
              <option value="PASS">PASS</option>
              <option value="FAIL">FAIL</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">비고 / 메모</label>
            <textarea
              className="w-full min-h-[100px] border rounded-md px-2 py-1 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </div>

        {/* 항목 테이블 (패턴2: 수량 + 비고) */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden min-w-0">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 min-w-0">
            <div className="font-medium text-sm min-w-0 truncate">검수 요청 항목 (QC)</div>
            {!readOnly && (
              <button className="btn-create-sm flex-shrink-0 whitespace-nowrap" onClick={addRow}>
                + 행 추가
              </button>
            )}
          </div>
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r._id} className="border rounded-lg p-3 bg-white space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">품목 정보</span>
                  {!readOnly && (
                    <button
                      className="px-2 py-1 rounded border text-xs whitespace-nowrap text-red-600 hover:bg-red-50"
                      onClick={() => removeRow(r._id)}
                    >
                      삭제
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">코드</label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.code || ""}
                      onChange={(e) =>
                        updateCell(r._id, "code", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="코드"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">품명</label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.name || ""}
                      onChange={(e) =>
                        updateCell(r._id, "name", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="품명"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">규격</label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.spec || ""}
                      onChange={(e) =>
                        updateCell(r._id, "spec", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="규격"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">단위</label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.unit || "EA"}
                      onChange={(e) =>
                        updateCell(r._id, "unit", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="단위"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">요청수량</label>
                    <input
                      type="number"
                      className="w-full rounded border px-2 py-1.5 text-right text-xs min-w-0"
                      value={r.qty}
                      onChange={(e) =>
                        updateCell(r._id, "qty", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="수량"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">비고</label>
                    <input
                      className="w-full rounded border px-2 py-1.5 text-xs min-w-0"
                      value={r.remark || ""}
                      onChange={(e) =>
                        updateCell(r._id, "remark", e.target.value)
                      }
                      disabled={readOnly}
                      placeholder="비고"
                    />
                  </div>
                </div>
              </div>
            ))}
            {rows.length > 0 && (
              <div className="border-t-2 border-gray-300 pt-2 mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2 text-right font-medium text-sm mb-1">합계</div>
                  <div className="col-span-2 space-y-1">
                    <label className="text-[10px] font-medium text-gray-500 block">총 요청수량</label>
                    <div className="text-xs text-right px-2 py-1.5 bg-gray-50 rounded border font-semibold">
                      {totalQty}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
